
import React, { useState } from 'react';
import { SimulationConfig, CustomMelody, MelodyData } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { AUDIO } from './AudioService';

interface AudioWorkshopTabProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const AudioWorkshopTab: React.FC<AudioWorkshopTabProps> = ({ config, setConfig }) => {
  const [manualMelodyString, setManualMelodyString] = useState<string>("");
  const [melodyName, setMelodyName] = useState<string>("");
  const [detectedVoices, setDetectedVoices] = useState<string[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>("");

  const applyManualMelody = (save: boolean = false) => {
    const voices = AUDIO.getAvailableVoices(manualMelodyString);
    let freqs: MelodyData = [];
    
    if (voices.length > 0) {
        setDetectedVoices(voices);
        setSelectedVoice(voices[0]);
        freqs = AUDIO.parseMelodyString(manualMelodyString, voices[0]);
    } else {
        setDetectedVoices([]);
        setSelectedVoice("");
        freqs = AUDIO.parseMelodyString(manualMelodyString);
    }

    if (freqs.length > 0) {
        if (save && melodyName.trim()) {
            const newMelody: CustomMelody = {
                id: Math.random().toString(36).substring(7),
                name: melodyName.trim(),
                frequencies: freqs
            };
            setConfig(prev => ({
                ...prev,
                customMelodies: [...prev.customMelodies, newMelody],
                selectedMelody: newMelody.name
            }));
            alert(`Melody "${melodyName}" saved to library!`);
            setMelodyName("");
        } else {
            setConfig(prev => ({ ...prev, customFrequencies: freqs, selectedMelody: 'Custom (Uploaded)' }));
        }
        AUDIO.resetIndex();
        AUDIO.playFullSequence(freqs.slice(0, 5), 0.15);
    }
  };

  const handleRandomize = () => {
    const freqs = AUDIO.generateRandomMelody(16);
    setManualMelodyString(freqs.map(f => {
        if (typeof f === 'number') return Math.round(f);
        return 0; 
    }).join(", "));
    setConfig(prev => ({ ...prev, customFrequencies: freqs, selectedMelody: 'Custom (Uploaded)' }));
    AUDIO.resetIndex();
    AUDIO.playFullSequence(freqs.slice(0, 8), 0.12);
  };

  const updateVoiceFilter = (v: string) => {
    setSelectedVoice(v);
    const freqs = AUDIO.parseMelodyString(manualMelodyString, v);
    setConfig(prev => ({ ...prev, customFrequencies: freqs, selectedMelody: 'Custom (Uploaded)' }));
    AUDIO.resetIndex();
  };

  return (
    <div className="flex flex-col gap-3 p-4 rounded-3xl border bg-orange-500/5 border-orange-500/20">
      <SectionLabel icon="fa-guitar" darkMode={config.darkMode}>Manual Melody Workshop</SectionLabel>
      
      <ControlGroup label="Melody Name (Optional)">
        <input 
          type="text" 
          value={melodyName}
          onChange={e => setMelodyName(e.target.value)}
          placeholder="Enter name to save..."
          className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
        />
      </ControlGroup>

      <ControlGroup label="Data Input">
        <textarea 
          value={manualMelodyString} 
          onChange={e => setManualMelodyString(e.target.value)}
          className={`w-full h-24 p-3 border rounded-xl text-[10px] font-mono outline-none transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
          placeholder={`Paste data here.\n\nSupported Formats:\n1. Simple List: 261, 329, 392...\n2. JSON Rhythm: [{"notes":[261],"duration":500}, ...]\n3. Online Sequencer Text Export`}
        />
      </ControlGroup>
      
      {detectedVoices.length > 0 && (
        <ControlGroup label="Voice Isolation">
            <div className="flex flex-wrap gap-2">
                {detectedVoices.map(v => (
                    <button key={v} onClick={() => updateVoiceFilter(v)} className={`px-2 py-1 rounded text-[9px] font-black border transition-all ${selectedVoice === v ? 'bg-orange-600 text-white border-orange-600' : (config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white')}`}>V-{v}</button>
                ))}
            </div>
        </ControlGroup>
      )}

      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => applyManualMelody(false)} className="w-full py-3 bg-neutral-600 text-white rounded-xl font-black text-[10px] uppercase hover:bg-neutral-700 shadow-lg shadow-neutral-500/20">Apply Temp</button>
        <button onClick={() => applyManualMelody(true)} className="w-full py-3 bg-orange-600 text-white rounded-xl font-black text-[10px] uppercase hover:bg-orange-700 shadow-lg shadow-orange-500/20" disabled={!melodyName.trim()}>Apply & Save</button>
      </div>
      <button onClick={handleRandomize} className="w-full py-3 bg-indigo-600 text-white rounded-xl font-black text-[10px] uppercase hover:bg-indigo-700 shadow-lg shadow-indigo-500/20">Randomize Notes</button>
    </div>
  );
};
